/**
 * Data access for the support entity (ТЗ §8, §9).
 *
 * Reads: ``GET /api/support/tickets/`` (my tickets), ``GET /api/support/tickets/<id>/``
 * (ticket detail incl. thread) and ``GET /api/faq/`` (public FAQ). Writes: open a ticket
 * and post a message. Tickets are object-scoped server-side (a foreign ticket yields 404).
 */
import { apiClient, useApiQuery, type QueryState } from "@shared/api";

import type {
  FaqItem,
  SupportCategory,
  SupportMessage,
  SupportTicketDetail,
  SupportTicketListItem,
} from "./model";

export const SUPPORT_TICKETS_PATH = "/support/tickets/";
export const FAQ_PATH = "/faq/";

/** Path of a single ticket's detail endpoint. */
export function ticketPath(ticketId: number): string {
  return `/support/tickets/${ticketId}/`;
}

/** Path of a ticket's customer-close endpoint (ТЗ №3 §8-10). */
export function ticketClosePath(ticketId: number): string {
  return `/support/tickets/${ticketId}/close/`;
}

/** Path of a ticket's messages endpoint (GET history / POST send). */
export function ticketMessagesPath(ticketId: number): string {
  return `/support/tickets/${ticketId}/messages/`;
}

/** Fetch the current user's support tickets (object-scoped server-side). */
export function useMyTickets(): QueryState<readonly SupportTicketListItem[]> {
  return useApiQuery<readonly SupportTicketListItem[]>(SUPPORT_TICKETS_PATH);
}

/** Fetch a single ticket's detail incl. its message thread (owner-only; 404 otherwise). */
export function useTicket(
  ticketId: number | null,
): QueryState<SupportTicketDetail> {
  return useApiQuery<SupportTicketDetail>(
    ticketId === null ? "" : ticketPath(ticketId),
    { enabled: ticketId !== null, deps: [ticketId] },
  );
}

/** Fetch the active FAQ items in display order (public, ТЗ §9). */
export function useFaq(): QueryState<readonly FaqItem[]> {
  return useApiQuery<readonly FaqItem[]>(FAQ_PATH);
}

/**
 * Open a support ticket with its first message (``POST /api/support/tickets/``, ТЗ §8).
 *
 * Returns the created ticket detail (status 201). The server assigns the human-readable
 * ``number`` and freezes the author identity.
 */
export function createTicket(input: {
  readonly category: SupportCategory;
  readonly text: string;
}): Promise<SupportTicketDetail> {
  return apiClient.post<SupportTicketDetail>(SUPPORT_TICKETS_PATH, {
    body: { category: input.category, text: input.text },
  });
}

/**
 * Close (archive) the user's own ticket (``POST /api/support/tickets/<id>/close/``, ТЗ №3 §8-10).
 *
 * Sets ``status=closed`` server-side; the ticket leaves the active list but its conversation is
 * preserved, and a later customer message reopens it. Returns the updated ticket detail.
 */
export function closeTicket(ticketId: number): Promise<SupportTicketDetail> {
  return apiClient.post<SupportTicketDetail>(ticketClosePath(ticketId), {});
}

/** Post a message into a ticket thread (``POST /api/support/tickets/<id>/messages/``). */
export function postTicketMessage(
  ticketId: number,
  text: string,
): Promise<SupportMessage> {
  return apiClient.post<SupportMessage>(ticketMessagesPath(ticketId), {
    body: { text },
  });
}
